/**
 * 파일명 : usr0104m.jsx
 * 설명 : 연차관리 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 *     
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0104mAction } from '../../../redux/slice/usr/usr0104m_slice';
import { IDeptCdState, IUserListItemState } from '../../../redux/models/usr/usr0104m_model';


/*  AntD  */
import { Button, Space, Row, Col, Layout, Typography, Select, DatePicker, ConfigProvider, Table  } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';
import dayjs from 'dayjs';

const Usr0104m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const deptCdList   = useAppSelector((state) => state.Usr0104m.deptCdList);
    const inquiryCnds  = useAppSelector((state) => state.Usr0104m.inquiryCnds);
    const userDatas    = useAppSelector((state) => state.Usr0104m.userDatas);

    let tmp = 0;
    const [tableKey, setTableKey] = React.useState<number>(0);
    const [contentHeight, setContentHeight] = React.useState<number>(window.innerHeight);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;


    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        const headerHeight = 250;  // Header 높이
        setContentHeight(window.innerHeight - headerHeight);

        const initInquiryCnds =  {
            empNo : '',
            userId : '',
            userNm : '',
            deptCd : '',
            userStCd : '',
            baseYy  : commonDate.maskDate(commonDate.getToday(), 'YYYY')  // 기준년월
        };

        dispatch(Usr0104mAction.setInquiryCnds(initInquiryCnds));

        const handleResize = () => {

            let tmpKey = ++tmp;
            tmp = tmpKey;
            setTableKey(tmpKey);
        };

        fn_DeptCds();

        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('resize', handleResize); // cleanup
        };
    }, []);

    React.useEffect(() => {
       if (userDatas && userDatas.length > 0) fn_Search();
    }, [inquiryCnds.baseYy]);

    React.useEffect(() => {
        if (deptCdList && deptCdList.length > 0) fn_Search();
     }, [deptCdList]);

    /**
     * 기능 : fn_DeptCds
     * 설명 : 부서코드 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_DeptCds = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            deptStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0005',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.deptItems.sort((a:IDeptCdState, b:IDeptCdState) => (a.deptNm > b.deptNm) ? 1 : -1)
                dispatch(Usr0104mAction.setDeptCdList(responseData.deptItems));
            },
        });
    };

    const deptOptions = React.useCallback(() => {
        let arrOptions: IDeptCdState[] = [];

        if (common.isAdmin()) {
            arrOptions.push({label: '전체', value: ''});
        }

        deptCdList.forEach((item:IDeptCdState) => {
            arrOptions.push({
                label: item.deptNm,
                value: item.deptCd
            });
        });

        return arrOptions;
    }, [deptCdList]);

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>성명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'center',
                width: 80,
                fixed: 'left',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: 80,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>기본연차</div>,
                key: 'giveOffDays',
                dataIndex: 'giveOffDays',
                width: 50,
                align: 'center',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>추가연차</div>,
                key: 'addOffDays',
                dataIndex: 'addOffDays',
                width: 50,
                align: 'center',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>총부여일</div>,
                dataIndex: 'tOffDays',
                key: 'tOffDays',
                align: 'center',
                width: 50,
                render: (_:string, record:IUserListItemState) => (record.giveOffDays) + (record.addOffDays)
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>사용일수</div>,
                dataIndex: 'useOffDays',
                key: 'useOffDays',
                align: 'center',
                width: 50,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>비고</div>,
                key: 'rmk',
                dataIndex: 'rmk',
                width: 300,
                align: 'center',
            },];
            return arr;
    }, []);

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Usr0104mAction.initState());
    };
    
    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Usr0104mAction.setUserDatas([]));
    };

    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
     const calendar_onChange = (_:any, dateStrings:any) => {
        dispatch(Usr0104mAction.setInquiryCnds({ ...inquiryCnds, baseYy : dateStrings }));
    };


    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = () => {
        fn_ResetResult();

        /* 변수선언 및 셋팅 영역 */
        const requestData = {
            ...inquiryCnds
        };
        
        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0005',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                dispatch(Usr0104mAction.setUserDatas(responseData.userList));
            },
        });
    };


    /**
      * 기능 : 입력 후 Enter key 입력 시 조회
      * 설명 : 입력컴포넌트
      * param : 
      * return : None
      */
     const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };


    return (
        <>
            <Layout>
                <Content>
                    <Row
                        align='middle'
                        justify='space-between'
                        style={{ marginTop: 10 }}
                    >
                        <Space>
                            <Col>
                                <Space>
                                    <ConfigProvider locale={locale}>
                                        <DatePicker
                                            value={dayjs(inquiryCnds.baseYy)}
                                            onChange={calendar_onChange}
                                            style={{ width: 80 }}
                                            picker='year'/>
                                    </ConfigProvider>
                                </Space>
                            </Col>
                            <Col>
                                <Space>
                                <label>부서</label>
                                    <Select
                                        size='small'
                                        style={{
                                            width: 200,
                                        }}
                                        optionFilterProp='label'
                                        options={deptOptions()}
                                        onKeyUp={handleOnKeyUp}
                                        onChange={val => {
                                            dispatch(Usr0104mAction.setInquiryCnds({...inquiryCnds, deptCd: val}))
                                        }}
                                        value={inquiryCnds.deptCd}
                                    />
                                </Space>
                            </Col>
                        </Space>
                        <Col flex='auto'>
                            <Space>
                                <Button
                                    size='small'
                                    name='btn_Search'
                                    className='background-color-primary-button'
                                    icon={<SearchOutlined />}
                                    onClick={onClick}
                                />
                            </Space>
                        </Col>
                    </Row>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>연차사용자목록</Text>
                            </Space>
                        </Col>
                    </Row>
                    <div key={tableKey} style={{ height: contentHeight, display: 'flex', flexDirection: 'column', width : '100%', flex: '1 1 auto', overflowY: 'auto' }}>
                        <Table
                            dataSource={userDatas}
                            key='empNo'
                            columns={columns()}
                            size='small'
                            style={{width:'100%'}}
                            scroll={{y:550}}
                        />
                    </div>
                </Content>
            </Layout>
        </>
    );
};

export default Usr0104m;

